import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Default visualization configuration
 */
const DEFAULT_CONFIG = {
  barCount: 32,
  barColor: 'rgb(59, 130, 246)', // Tailwind blue-500
  inactiveColor: 'rgb(209, 213, 219)', // Gray-300
  minHeightPercent: 0.25,
  maxHeightPercent: 0.8,
  // 1024 gives 512 usable bins, so each of the 32 bars averages 16 of them.
  // At 256 a bar averaged 2 bins, and two samples of a noisy spectrum jump
  // around violently frame to frame — the single biggest source of jitter.
  fftSize: 1024,
  // The analyser's own temporal smoothing. 0.3 is close to raw; 0.8 is the
  // usual choice for a visualization rather than a measurement.
  smoothingTimeConstant: 0.8,
  // Per-bar easing applied on top: the fraction of the remaining distance
  // covered per 60Hz frame. Lower is smoother but laggier; 0.25 settles in
  // ~80ms, which still reads as responsive to speech. Scaled by real frame
  // time at runtime, so a 120Hz display eases over the same wall-clock
  // duration rather than twice as fast.
  smoothingFactor: 0.25,
};

/**
 * Custom hook for audio waveform visualization using Canvas and Web Audio API
 * Creates a frequency bar visualization that responds to microphone input
 *
 * @param {Object} options
 * @param {React.RefObject} options.canvasRef - Ref to canvas element
 * @param {boolean} options.isRecording - Whether currently recording (controls animation)
 * @param {Object} options.config - Visualization configuration
 * @returns {Object} Visualization controls and state
 */
const useAudioVisualization = (options = {}) => {
  const { canvasRef, isRecording = false, config = {} } = options;

  // Memoize visualization config to prevent unnecessary re-renders
  const visualConfig = useMemo(
    () => ({ ...DEFAULT_CONFIG, ...config }),
    [
      config.barCount,
      config.barColor,
      config.inactiveColor,
      config.minHeightPercent,
      config.maxHeightPercent,
      config.fftSize,
      config.smoothingTimeConstant,
      config.smoothingFactor,
    ]
  );

  // State
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);

  // Refs
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  // Eased bar heights carried between frames, so each bar glides toward its
  // target instead of snapping to it.
  const barHeightsRef = useRef(null);
  // Timestamp of the previous drawn frame, so easing can be scaled by real
  // elapsed time instead of assuming a fixed refresh rate.
  const lastFrameTimeRef = useRef(0);
  const isRecordingRef = useRef(isRecording);
  // In-flight initialize() promise, so overlapping callers share one
  // getUserMedia rather than each acquiring their own stream.
  const initializingRef = useRef(null);

  // Keep isRecording ref in sync
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  /**
   * Draw waveform bars on canvas
   */
  const drawWaveform = useCallback(() => {
    if (!canvasRef?.current || !analyserRef.current || !dataArrayRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = canvas;
    const { barCount, barColor, minHeightPercent, maxHeightPercent, smoothingFactor } =
      visualConfig;

    // Clear canvas
    ctx.fillStyle = 'rgb(255, 255, 255)';
    ctx.fillRect(0, 0, width, height);

    // Get frequency data
    analyserRef.current.getByteFrequencyData(dataArrayRef.current);

    // Calculate bar dimensions
    const barWidth = Math.ceil((width / barCount) * 0.35);
    const spacing = Math.ceil((width - barCount * barWidth) / (barCount - 1));
    const maxBarHeight = height * maxHeightPercent;
    const minHeight = height * minHeightPercent;

    if (!barHeightsRef.current || barHeightsRef.current.length !== barCount) {
      barHeightsRef.current = new Array(barCount).fill(minHeight);
      lastFrameTimeRef.current = 0;
    }

    // Convert the per-60Hz-frame easing into one scaled by actual frame time,
    // so the animation covers the same ground per millisecond on a 60Hz and a
    // 120Hz display. Clamped so a long stall (backgrounded tab, GC pause)
    // snaps to the target rather than overshooting.
    const now = performance.now();
    const deltaMs = lastFrameTimeRef.current ? now - lastFrameTimeRef.current : 1000 / 60;
    lastFrameTimeRef.current = now;
    const easing =
      deltaMs >= 250 ? 1 : 1 - Math.pow(1 - smoothingFactor, Math.min(deltaMs, 250) / (1000 / 60));

    ctx.fillStyle = barColor;

    // Draw bars
    for (let i = 0; i < barCount; i++) {
      const startIndex = Math.floor((i / barCount) * (dataArrayRef.current.length / 2));
      const endIndex = Math.floor(((i + 1) / barCount) * (dataArrayRef.current.length / 2));

      let sum = 0;
      for (let j = startIndex; j < endIndex; j++) {
        sum += dataArrayRef.current[j];
      }
      // endIndex === startIndex when there are fewer bins than bars; guard so a
      // narrow FFT yields a flat bar rather than NaN, which draws nothing.
      const span = endIndex - startIndex;
      const average = span > 0 ? sum / span : 0;

      const variableHeight = (average / 255) * (maxBarHeight - minHeight);
      const targetHeight = minHeight + variableHeight;

      // Ease toward the target rather than jumping to it.
      const previous = barHeightsRef.current[i];
      const easedHeight = previous + (targetHeight - previous) * easing;
      barHeightsRef.current[i] = easedHeight;

      const x = i * (barWidth + spacing);
      const y = (height - easedHeight) / 2;

      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, easedHeight, barWidth / 2);
      ctx.fill();
    }

    // Driven purely by requestAnimationFrame. The previous setTimeout-then-rAF
    // pairing produced uneven frame spacing — the timer and the vsync clock
    // beat against each other, so frames arrived in bursts, which reads as
    // stutter no matter how smooth the underlying values are.
    if (isRecordingRef.current) {
      animationFrameIdRef.current = requestAnimationFrame(drawWaveform);
    }
  }, [canvasRef, visualConfig]);

  /**
   * Draw static (inactive) waveform
   */
  const drawStaticWaveform = useCallback(() => {
    if (!canvasRef?.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    const { barCount, inactiveColor, minHeightPercent } = visualConfig;

    ctx.fillStyle = 'rgb(255, 255, 255)';
    ctx.fillRect(0, 0, width, height);

    const barWidth = Math.ceil((width / barCount) * 0.35);
    const spacing = Math.ceil((width - barCount * barWidth) / (barCount - 1));

    ctx.fillStyle = inactiveColor;

    for (let i = 0; i < barCount; i++) {
      const staticHeight = height * (minHeightPercent + Math.sin(i * 0.3) * 0.05);
      const x = i * (barWidth + spacing);
      const y = (height - staticHeight) / 2;

      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, staticHeight, barWidth / 2);
      ctx.fill();
    }
  }, [canvasRef, visualConfig]);

  /**
   * Initialize audio context and analyzer
   */
  const initialize = useCallback(async () => {
    if (isInitialized) return { success: true };

    // Guard against overlapping calls. The `isInitialized` state check above is
    // not sufficient: it reads a value captured at render time, so two calls
    // issued before the first resolves both pass it, both call getUserMedia,
    // and the second overwrites streamRef — orphaning the first stream where
    // cleanup() can never find it, leaving the mic indicator on for good.
    if (initializingRef.current) return initializingRef.current;

    const pending = (async () => {
      let stream = null;
      try {
        // Request microphone access with raw audio for visualization
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
        });

        // Create audio context
        const context = new (window.AudioContext || window.webkitAudioContext)();

        // Store the stream ref before any further await. On a cold page load
        // there is no sticky activation, so context.resume() below can stay
        // pending indefinitely under the autoplay policy — if the stream were
        // only recorded after that await, cleanup() could never release it.
        streamRef.current = stream;

        if (context.state === 'suspended') {
          // Don't block initialization on this: an un-resumed context yields a
          // flat waveform, which is cosmetic, whereas hanging here leaves the
          // mic permanently held.
          context.resume().catch(() => {});
        }

        // Create analyzer
        const analyser = context.createAnalyser();
        analyser.fftSize = visualConfig.fftSize;
        analyser.smoothingTimeConstant = visualConfig.smoothingTimeConstant;
        analyser.minDecibels = -100;
        analyser.maxDecibels = -30;

        // Connect stream to analyzer
        const source = context.createMediaStreamSource(stream);
        source.connect(analyser);

        // Create data array
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        // Store refs
        audioContextRef.current = context;
        analyserRef.current = analyser;
        dataArrayRef.current = dataArray;

        setIsInitialized(true);
        setError(null);

        return { success: true, context, analyser, dataArray, stream };
      } catch (err) {
        // Release the mic if we acquired it but failed to wire it up.
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
        console.error('Error initializing audio visualization:', err);
        setError('Could not access microphone. Please check your permissions.');
        return { success: false, error: err };
      }
    })();

    initializingRef.current = pending;
    try {
      return await pending;
    } finally {
      initializingRef.current = null;
    }
  }, [isInitialized, visualConfig.fftSize, visualConfig.smoothingTimeConstant]);

  /**
   * Cleanup resources
   */
  const cleanup = useCallback(() => {
    // Stop animation
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }

    // Stop stream tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current?.state !== 'closed') {
      audioContextRef.current?.close().catch(console.error);
    }
    audioContextRef.current = null;
    analyserRef.current = null;
    dataArrayRef.current = null;
    // Discard the eased heights, or the next recording opens by animating down
    // from wherever the last one happened to stop.
    barHeightsRef.current = null;
    lastFrameTimeRef.current = 0;

    setIsInitialized(false);
  }, []);

  // Handle recording state changes - start/stop animation
  useEffect(() => {
    if (isRecording && isInitialized && analyserRef.current && dataArrayRef.current) {
      // Resume audio context if needed
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current
          .resume()
          .then(() => {
            drawWaveform();
          })
          .catch(console.error);
      } else {
        drawWaveform();
      }
    } else {
      // Stop animation
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
      // Draw static waveform when not recording
      drawStaticWaveform();
    }

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    };
  }, [isRecording, isInitialized, drawWaveform, drawStaticWaveform]);

  // Draw static waveform initially
  useEffect(() => {
    if (!isRecording && canvasRef?.current) {
      drawStaticWaveform();
    }
  }, [isRecording, canvasRef, drawStaticWaveform]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    // State
    isInitialized,
    error,

    // Controls
    initialize,
    cleanup,
    drawWaveform,
    drawStaticWaveform,

    // Refs for external access
    audioContextRef,
    analyserRef,
    streamRef,
  };
};

export default useAudioVisualization;
